import * as Sentry from '@sentry/react';
import { createContext, useState } from 'react';
import { BSON } from 'realm-web';
import { OriginalUserDataType, UserResponse } from '@/types';
import { fetchCompany, transformData } from './services';
import { Company, UserContextType } from './types';
import { getCompanyById, updateCompany as updateCompanyData } from '@/services';
import { jwtDecode } from 'jwt-decode';
import { FINAL_USERS } from '@/enums';
import { userLogout } from '@final-commerce/jwt-storage-lib';
import { redirectToLoginInHub } from '@/pages/private/helpers';

export const UserContext = createContext<UserContextType | undefined>(undefined);

type UserProviderProps = {
  children: React.ReactNode;
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<OriginalUserDataType | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [currentUser, setCurrentUser] = useState<UserResponse>({
    accountId: '',
    companyId: '',
  } as UserResponse);

  const updateAndRefetchCompany = async (id: string, body: Partial<Company>) => {
    await updateCompanyData(id, body);
    const fetchedCompany = (await fetchCompany(new BSON.ObjectID(id))) as Company;
    setCompany(fetchedCompany);
  };

  const updateCompany = async (companyId: string) => {
    const userData = jwtDecode(
      localStorage.getItem('accessToken') as string,
    ) as OriginalUserDataType;

    let organizationId = userData.targetOrganizationId;
    if (FINAL_USERS.includes(userData.type)) {
      organizationId = companyId;
    }
    const fetchedCompany = (await getCompanyById(companyId, organizationId)) as Company;

    // `?onboard` is a legacy-builder entry point; the Flow app has no handler
    // for it and its /onboarding route bounces already-authenticated users.
    // Stays on the legacy host until Flow exposes an equivalent.
    if (!fetchedCompany?.registrationCompleted) {
      window.location.href = `${import.meta.env.VITE_LEGACY_BUILDER_URL}?onboard`;
    }
    setCompany(fetchedCompany);

    return fetchedCompany;
  };

  const fetchUser = async () => {
    const userData = jwtDecode(
      localStorage.getItem('accessToken') as string,
    ) as OriginalUserDataType;
    setUser(userData);
    const newUser = await transformData(userData as OriginalUserDataType);
    await updateCompany(newUser.companyId);
    setCurrentUser(newUser as UserResponse);
    Sentry.setUser({
      id: newUser._id,
      account_id: newUser.accountId,
      company_id: newUser.companyId,
      user_type: newUser.type,
    });
    return newUser;
  };

  const logOutUser = async () => {
    try {
      await userLogout();
    } catch (error) {
      console.error('logOutUser', error);
    }
    setCurrentUser({} as UserResponse);
    setCompany(null);
    setUser(null);
    redirectToLoginInHub();
  };

  const value = {
    user,
    setUser,
    fetchUser,
    setCurrentUser,
    setCompany,
    logOutUser,
    currentUser,
    company,
    updateCompany,
    updateAndRefetchCompany,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
